import React from "react";
import useExcelUpload from "../../../../utils/hooks/useExcelUpload";
import UploadExcelDialog from "../../utils/UploadExcelEnhanced";

const fixedSheetName = "HP breakdown";
const fixedColumns = [
  [
    "Asset Code",
    "Asset Name",
    "Cost",
    "CA Type",
    "No. of years of claims",
    "YA of Principal Paid",
    "Principal Paid",
  ],
];

const instructions = [
  ["Instructions"],
  [
    "Asset Code",
    "Enter a unique identifier for the asset (e.g., HPA001, XYZ001). If the code matches an existing asset, the system will update that asset. If it does not match, a new asset will be created.",
  ],
  [
    "Asset Name",
    "Provide a descriptive name for the asset (e.g., Laptop, Server Rack).",
  ],
  ["Cost", "Enter the acquisition cost of the asset. Use numeric values only."],
  [
    "CA Type",
    "Specify the capital allowance type. See list in <CA Type Options> worksheet.",
  ],
  ["No. of Years", "Enter the number of years over which claims can be made."],
  [
    "YA of Principal Paid",
    "Enter the Year of Assessment (e.g., 2025, 2026) for which the principal payment is made.",
  ],
  [
    "Principal Paid",
    "Enter the amount of principal paid for the corresponding year. Use numeric values only.",
  ],
  [],
  [
    "Notes",
    "• The system will only use the last occurrence of each Asset Code to update asset information (Asset Name, Cost, CA Type, No. of Years).",
  ],
  [
    "",
    "• If a line does not have an Asset Code, the system will skip processing it.",
  ],
  [
    "",
    "• Subsequent rows with the same Asset Code will be treated as claim/payment breakdowns. If the Year is the same as the previous entry for that Asset Code, the last recorded amount will be used.",
  ],
  [
    "",
    "• If an Asset Code does not match any existing asset, a new asset will be created.",
  ],
  [
    "",
    "• To avoid confusion, ensure Asset Codes are consistent and unique across your file.",
  ],
  [
    "",
    `• The system is configured to read only the sheet titled '${fixedSheetName}'.`,
  ],
];

const buildAssetMapSheet = (assets, assetMap) => {
  const header = [
    "Asset Code",
    "Asset Name",
    "CA Type",
    "No. of Years",
    "Cost",
  ];

  const assetCodeLookup = new Map(
    assetMap.map((a) => [a.assetId, a.assetCode])
  );

  const rows = assets.map((asset) => [
    assetCodeLookup.get(asset.assetId) || "",
    asset.assetName,
    asset.caType,
    asset.noOfYears,
    asset.cost,
  ]);

  return [header, ...rows];
};

const HirePurchaseExcelUpload = ({
  onExcelAddRows,
  open,
  assets,
  assetCodeList,
  caTypeOptions,
  handleCloseDialog,
}) => {
  const {
    selectedFile,
    uploading,
    alertMessage,
    alertSeverity,
    errorFileUrl,
    rowCount,
    handleCloseAlert,
    handleDownloadMultipleTemplate,
    handleFileMultiSheetChange,
    handleResetFile,
    handleUpload,
  } = useExcelUpload({
    fileName: `hire_purchase_upload_template.xlsx`,
    sheets: [
      {
        name: fixedSheetName,
        data: fixedColumns,
        columnWidths: [18],
        type: "aoa",
      },
      {
        name: "Existing HP Assets",
        data: buildAssetMapSheet(assets, assetCodeList),
        columnWidths: [18],
        type: "aoa",
      },
      {
        name: "CA Type Options",
        data: caTypeOptions.map((opt) => [opt]),
        columnWidths: [18],
        type: "aoa",
      },
      {
        name: "Instructions",
        data: instructions,
        columnWidths: [18],
        type: "aoa",
      },
    ],
    validationSchema: [
      { type: "text", required: true },
      { type: "text", required: true },
      { type: "number", round: true, required: true },
      { type: "text", required: true, fixedValue: caTypeOptions },
      { type: "number", round: true, required: true },
      { type: "number", round: true, required: true },
      { type: "number", round: true, required: true },
    ],
    onUpload: (excelData) => {
      if (excelData[fixedSheetName] && excelData[fixedSheetName].length > 0) {
        const data = excelData[fixedSheetName];

        const columns = [
          "assetCode",
          "assetName",
          "cost",
          "caType",
          "noOfYears",
          "principalPaidYear",
          "principalPaidAmount",
        ];

        const mappedData = data.map((row) =>
          Object.fromEntries(columns.map((key, index) => [key, row[index]]))
        );

        onExcelAddRows(mappedData);
      }
    },
  });

  return (
    <UploadExcelDialog
      open={open}
      selectedFile={selectedFile}
      uploading={uploading}
      alertMessage={alertMessage}
      alertSeverity={alertSeverity}
      errorFileUrl={errorFileUrl}
      validRows={rowCount}
      onClose={handleCloseDialog}
      onDownloadTemplate={handleDownloadMultipleTemplate}
      onFileChange={(event) =>
        handleFileMultiSheetChange(event, [fixedSheetName])
      }
      onUpload={handleUpload}
      onCloseAlert={handleCloseAlert}
      onReset={handleResetFile}
    />
  );
};

export default HirePurchaseExcelUpload;
